import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { Flyout, FlyoutHeader, TabNav } from 'components';
import Bell from 'icons/indexed-icons/Bell';
import theme from 'themes';
import Accordion from './Accordion';
import NoNotifications from './NoNotifications';
import { useTranslation } from 'react-i18next';
import {
  useGetUserQuery,
  useGetAlertsByUserIdQuery,
  useGetUserCartNotificationsQuery,
  useUpdateUserCartNotificationsMutation,
  useDeleteUserCartNotificationsMutation,
  useMarkAllUserCartNotificationsReadMutation
} from 'api';
import { AlertTriggers, CartNotification, CartPriceChangeAlert } from 'types/machine-health/alerts';
import { useTimeZone } from 'providers';
import {
  NotificationIconContainer,
  TabContainer,
  FlyoutContent,
  BellIconBadge,
  Badge
} from './index.elements';
import { useAuthToken } from 'selectors';
import OrderPdfModal from 'components/NewSFOrdersTable/OrderPdfModal';
import { saveAs } from 'file-saver';
import {
  getNotificationId,
  getNotificationOrderNumber,
  getNotificationSourceDate,
  organizeNotificationsByDate,
  OrganizedAlerts
} from './utils';

interface Tab {
  isTabEnabled: boolean;
  title: string | ReactElement;
}

const Notification = (): ReactElement => {
  const { t } = useTranslation(['fpns']);
  const [activeTab, setActiveTab] = useState<'0' | '1' | string>('0');
  const [openNotification, setOpenNotification] = useState(false);
  const authToken = useAuthToken();
  const [activeAlerts, setActiveAlerts] = useState<OrganizedAlerts>({
    today: [],
    yesterday: [],
    older: []
  });
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [linkToPdf, setLinkToPdf] = useState<string | undefined>(undefined);
  const [poNumber, setPoNumber] = useState<string | undefined>(undefined);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);
  const { timeZone } = useTimeZone();

  const { data: cartNotifications, refetch: refetchCartNotifications } =
    useGetUserCartNotificationsQuery();
  const [updateCartNotifications] = useUpdateUserCartNotificationsMutation();
  const [deleteCartNotifications] = useDeleteUserCartNotificationsMutation();
  const [markAllNotificationsRead] = useMarkAllUserCartNotificationsReadMutation();

  // Get current user data
  const { data: userData } = useGetUserQuery();

  // Get alerts using query
  const { refetch } = useGetAlertsByUserIdQuery(
    {
      userId: userData?.id || '',
      groupId: ''
    },
    {
      skip: !userData?.id // Skip the query if we don't have userId
    }
  );
  useEffect(() => {
    if (authToken) {
      refetchCartNotifications(); // Refetch alerts when authToken changes (e.g., on login)
    }
  }, [authToken]);
  const queryAlerts = useMemo<AlertTriggers[]>(
    () =>
      (cartNotifications?.notifications || []).map((notification) => ({
        ...notification,
        start: getNotificationSourceDate(notification)
      })),
    [cartNotifications]
  );

  const organizedAlerts = useMemo(
    () => organizeNotificationsByDate(queryAlerts, timeZone),
    [queryAlerts, timeZone]
  );

  const totalAlerts =
    activeAlerts.today.length + activeAlerts.yesterday.length + activeAlerts.older.length;

  const TABS: Record<string, Tab> = {
    important: {
      isTabEnabled: true,
      title: (
        <>
          {t('important_tab')} <Badge isActive={activeTab === '0'}>{totalAlerts}</Badge>
        </>
      )
    },
    alternative: {
      isTabEnabled: true,
      title: (
        <>
          {t('alternative_tab')} <Badge isActive={activeTab === '1'}>{0}</Badge>
        </>
      )
    }
  };

  useEffect(() => {
    setActiveTab('0');
  }, []);
  useEffect(() => {
    setActiveAlerts(organizedAlerts);
    setUnreadNotificationCount(cartNotifications?.unreadCount || 0);
  }, [cartNotifications, organizedAlerts]);

  const applyNotificationResponse = (notificationResponse?: CartNotification) => {
    if (!notificationResponse) return;

    const notifications = (notificationResponse.notifications || []).map((notification) => ({
      ...notification,
      start: getNotificationSourceDate(notification)
    }));

    setActiveAlerts(organizeNotificationsByDate(notifications, timeZone));
    setUnreadNotificationCount(notificationResponse.unreadCount || 0);
  };

  const navItem = (tab: Tab, index: '0' | '1') => {
    return {
      label: tab.title,
      onClick: () => {
        setActiveTab(index);
      },
      active: activeTab === index,
      isTabEnabled: tab.isTabEnabled
    };
  };

  const onClickNotification = () => {
    const nextOpenState = !openNotification;
    setOpenNotification(nextOpenState);

    if (!nextOpenState) {
      return;
    }

    refetch(); // Refetch alerts when notification is opened

    if (cartNotifications && cartNotifications.unreadCount > 0) {
      const notificationIds = cartNotifications.notifications
       .map((notification) => getNotificationId(notification))
       .filter((id): id is string => Boolean(id));

      if (notificationIds.length === 0) {
       return;
      }

      markAllNotificationsRead({ notificationIds })
       .unwrap()
       .then((res) => {
         applyNotificationResponse(res);
         refetchCartNotifications();
       })
       .catch((error) => {
         console.error('Error marking notifications as read:', error);
        });
    }
  };
  const updateNotificationStatus = (id: string) => {
    updateCartNotifications(id)
      .unwrap()
      .then((res) => {
        applyNotificationResponse(res);
        refetchCartNotifications();
      })
      .catch((error) => {
        console.error('Error updating notification status:', error);
      });
  };
  const deleteNotification = (id?: string) => {
    if (id) {
      deleteCartNotifications(id)
        .unwrap()
        .then((res) => {
          applyNotificationResponse(res);
          refetchCartNotifications();
        })
        .catch((error) => {
          console.error('Error deleting notification:', error);
        });
    }
  };
  const downloadOrderDetails = () => {
    saveAs(linkToPdf as string, `order-quote-${poNumber}.pdf`);
  };
  const hideOrderModal = () => {
    setShowOrderDetails(!showOrderDetails);
  };
  const showNotificationDetails = (type: string, customData?: CartPriceChangeAlert): void => {
    switch (type) {
      case 'PRICE_CHANGE':
        // Logic to show price change details
        console.log('Viewing Price Change Details...');
        break;
      case 'ORDER_PLACED':
        // Logic to view order details
        if (customData?.linkToDocument) {
          setLinkToPdf(customData.linkToDocument);
          setPoNumber(getNotificationOrderNumber(customData));
          setShowOrderDetails(true);
        } else {
          console.log('No link to document available for this order.');
        }
        break;
      case 'QUOTE_SUBMITTED':
        // Logic to show quote details
        console.log('Viewing Quote Details...');
        break;
      default:
        break;
    }
  };
  return (
    <>
      <NotificationIconContainer onClick={onClickNotification} openNotification={openNotification}>
        <Bell
          color={openNotification ? theme.colors.primaryBlue5 : theme.colors.lightGrey10}
          strokeWidth="1.6"
        />
        {unreadNotificationCount > 0 && (
          <BellIconBadge>{unreadNotificationCount}</BellIconBadge>
        )}
      </NotificationIconContainer>
      <>
        {showOrderDetails && (
          <OrderPdfModal
            showOrderDetails={showOrderDetails}
            hideOrderModal={hideOrderModal}
            downloadOrderDetails={downloadOrderDetails}
            t={t}
            fileUrl={linkToPdf}
          />
        )}
      </>
      {openNotification && (
        <Flyout
          width="28.125rem"
          visible={openNotification}
          onClose={onClickNotification}
          startsBelowHeader={true}
        >
          <FlyoutHeader heading={t('notifications')} onClose={onClickNotification} />
          <FlyoutContent>
            <TabContainer>
              <TabNav items={[navItem(TABS.important, '0'), navItem(TABS.alternative, '1')]} />
            </TabContainer>
            <div>
              {activeTab === '0' && (
                <div>
                  {totalAlerts === 0 ? (
                    <NoNotifications />
                  ) : (
                    <>
                      <Accordion
                        title={t('today')}
                        isOpenInitially={true}
                        updateNotificationStatus={updateNotificationStatus}
                        deleteNotification={deleteNotification}
                        showNotificationDetails={showNotificationDetails}
                      >
                        {activeAlerts.today}
                      </Accordion>
                      <Accordion
                        title={t('yesterday')}
                        isOpenInitially={true}
                        updateNotificationStatus={updateNotificationStatus}
                        deleteNotification={deleteNotification}
                        showNotificationDetails={showNotificationDetails}
                      >
                        {activeAlerts.yesterday}
                      </Accordion>
                      <Accordion
                        title={t('older')}
                        isOpenInitially={false}
                        updateNotificationStatus={updateNotificationStatus}
                        deleteNotification={deleteNotification}
                        showNotificationDetails={showNotificationDetails}
                      >
                        {activeAlerts.older}
                      </Accordion>
                    </>
                  )}
                </div>
              )}
              {activeTab === '1' && (
                <div>
                  <Accordion
                    title={t('today')}
                    isOpenInitially={true}
                    isAlternative={true}
                  >
                    {[]}
                  </Accordion>
                  <Accordion
                    title={t('yesterday')}
                    isOpenInitially={true}
                    isAlternative={true}
                  >
                    {[]}
                  </Accordion>
                  <Accordion
                    title={t('older')}
                    isOpenInitially={false}
                    isAlternative={true}
                  >
                    {[]}
                  </Accordion>
                </div>
              )}
            </div>
          </FlyoutContent>
        </Flyout>
      )}
    </>
  );
};

export default Notification;
